import { motion } from 'framer-motion'
import {
  Activity,
  BarChart3,
  CheckCircle,
  Clock,
  Eye,
  EyeOff,
  FileText,
  Search,
  TrendingUp,
  Users
} from 'lucide-react'
import { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'
import { ThemeContext } from '../contexts/ThemeContext'
import { Badge } from './ui/badge'
import { Button } from './ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'
import { Progress } from './ui/progress'
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs'

const Dashboard = () => {
  const navigate = useNavigate()
  const { isDarkMode: darkMode } = useContext(ThemeContext)

  // 案件狀態翻譯映射
  const translateCaseStatus = (status) => {
    const statusMap = {
      'draft': '草稿',
      'submitted': '已提交',
      'under_review': '審查中',
      'approved': '已批准',
      'granted': '已核准',
      'rejected': '已駁回',
      'expired': '已到期',
      'abandoned': '已放棄'
    }
    return statusMap[status] || status
  }

  const [systemStats, setSystemStats] = useState({
    totalCases: 0,
    activeCases: 0,
    completedCases: 0,
    pendingDeadlines: 0,
    monthlySearches: 0,
    systemUptime: 0,
    activeUsers: 0
  })

  const [recentActivities, setRecentActivities] = useState([])
  const [monthlyData, setMonthlyData] = useState([])
  const [caseStatusData, setCaseStatusData] = useState([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [showSystemActivities, setShowSystemActivities] = useState(false)
  const [showBackendActivities, setShowBackendActivities] = useState(false)

  // 載入統計數據函數
  const fetchStats = async (showLoading = false) => {
    try {
      if (showLoading) {
        setLoading(true)
      } else {
        setRefreshing(true)
      }

      // 並行載入所有統計數據
      const [statsResponse, trendsResponse, activitiesResponse, caseStatusResponse] = await Promise.all([
        fetch('/api/stats/dashboard'),
        fetch('/api/stats/monthly-trends'),
        fetch('/api/stats/recent-activities'),
        fetch('/api/stats/case-status-distribution')
      ])

      if (statsResponse.ok) {
        const statsData = await statsResponse.json()
        setSystemStats(statsData.data || statsData)
      }

      if (trendsResponse.ok) {
        const trendsData = await trendsResponse.json()
        setMonthlyData(trendsData.data || trendsData)
      }

      if (activitiesResponse.ok) {
        const activitiesData = await activitiesResponse.json()
        setRecentActivities(activitiesData.data || activitiesData)
      }

      if (caseStatusResponse.ok) {
        const caseStatusResponseData = await caseStatusResponse.json()
        const translatedData = (caseStatusResponseData.data || caseStatusResponseData).map(item => ({
          ...item,
          name: translateCaseStatus(item.name)
        }))
        setCaseStatusData(translatedData)
      }

    } catch (error) {
      console.error('載入統計數據失敗:', error)
    } finally {
      if (showLoading) {
        setLoading(false)
      } else {
        setRefreshing(false)
      }
    }
  }

  // 載入統計數據
  useEffect(() => {
    fetchStats(true) // 初次載入顯示載入狀態

    // 每 10 分鐘自動刷新數據 (不顯示載入狀態，避免閃爍)
    const interval = setInterval(() => fetchStats(false), 600000)
    return () => clearInterval(interval)
  }, [])

  const StatCard = ({ icon: Icon, title, value, change, changeType, description, onClick, alertLevel }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="h-full"
    >
      <Card
        className={`h-full flex flex-col hover:shadow-lg transition-shadow duration-300 ${onClick ? 'cursor-pointer hover:border-blue-300 dark:hover:border-blue-500' : ''
          } ${alertLevel === 'warning' ? 'ring-2 ring-yellow-200 dark:ring-yellow-600 border-yellow-300 dark:border-yellow-500' :
            alertLevel === 'danger' ? 'ring-2 ring-red-200 dark:ring-red-600 border-red-300 dark:border-red-500' : ''
          }`}
        onClick={onClick}
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-gray-700 dark:text-gray-200">{title}</CardTitle>
          <Icon className={`h-4 w-4 ${alertLevel === 'warning' ? 'text-yellow-500 dark:text-yellow-400' :
            alertLevel === 'danger' ? 'text-red-500 dark:text-red-400' :
              'text-gray-500 dark:text-gray-400'
            }`} />
        </CardHeader>
        <CardContent className="flex-1 flex flex-col justify-between">
          <div className={`text-2xl font-bold text-center ${alertLevel === 'warning' ? 'text-yellow-600 dark:text-yellow-400' :
            alertLevel === 'danger' ? 'text-red-600 dark:text-red-400' :
              'text-gray-900 dark:text-gray-50'
            }`}>{value}</div>
          <div className="mt-2 space-y-1">
            {change && (
              <div className="flex items-center space-x-1">
                <TrendingUp className={`h-3 w-3 ${changeType === 'positive' ? 'text-green-500 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`} />
                <span className={`text-xs font-medium ${changeType === 'positive' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {change}
                </span>
                <span className="text-xs text-gray-600 dark:text-gray-300">較上月</span>
              </div>
            )}
            <div className="min-h-[2.5rem] flex items-end">
              {description && (
                <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">{description}</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  )

  const ActivityItem = ({ activity }) => {
    const getIcon = (type) => {
      switch (type) {
        case 'search': return Search
        case 'case': return FileText
        case 'deadline': return Clock
        default: return Activity
      }
    }

    const getStatusColor = (status) => {
      switch (status) {
        case 'completed': return 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-700'
        case 'warning': return 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300 border border-yellow-200 dark:border-yellow-700'
        case 'new': return 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 border border-blue-200 dark:border-blue-700'
        default: return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-600'
      }
    }

    const getNavigationPath = (type) => {
      switch (type) {
        case 'search': return '/patent-search'
        case 'case': return '/case-management'
        case 'deadline': return '/case-management'
        default: return '/dashboard'
      }
    }

    const Icon = getIcon(activity.type)

    return (
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="flex items-start space-x-3 p-4 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors cursor-pointer border border-transparent hover:border-gray-200 dark:hover:border-gray-600"
        onClick={() => navigate(getNavigationPath(activity.type))}
      >
        <div className="flex-shrink-0">
          <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/50 rounded-full flex items-center justify-center">
            <Icon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate pr-3">
              {activity.title}
            </p>
            <Badge className={`text-xs font-medium px-2 py-1 ml-2 flex-shrink-0 ${getStatusColor(activity.status)}`}>
              {activity.status === 'completed' ? '已完成' :
                activity.status === 'warning' ? '警告' :
                  activity.status === 'new' ? '新建' : '處理中'}
            </Badge>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-300 truncate mb-2">{activity.description}</p>
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">{activity.user}</span>
            <span className="text-xs text-gray-500 dark:text-gray-400">{activity.time}</span>
          </div>
        </div>
      </motion.div>
    )
  }

  return (
    <div className="space-y-6">
      {/* 頁面標題 */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">儀表板</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            歡迎使用智財權助理平台
            {refreshing && (
              <span className="ml-2 text-xs text-blue-500 dark:text-blue-400 animate-pulse">
                • 數據更新中
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Badge variant="outline" className="text-green-600 border-green-600">
            <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
            {'系統運行正常'}
          </Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchStats(true)}
            disabled={loading || refreshing}
          >
            <Activity className="h-4 w-4 mr-2" />
            {loading || refreshing ? '更新中' : '刷新數據'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/reports')}
          >
            <BarChart3 className="h-4 w-4 mr-2" />
            匯出報告
          </Button>
        </div>
      </motion.div>

      {/* 統計卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 auto-rows-fr">
        <StatCard
          icon={FileText}
          title={'案件總數'}
          value={loading ? '載入中...' : (systemStats.totalCases || '0')}
          description={`系統中有 ${systemStats.totalCases || 0} 個案件`}
          onClick={() => navigate('/case-management')}
        />
        <StatCard
          icon={Search}
          title={'本月搜尋'}
          value={loading ? '載入中...' : (systemStats.monthlySearches || '0')}
          description={`已執行 ${systemStats.monthlySearches || 0} 次搜尋`}
          onClick={() => navigate('/patent-search')}
        />
        <StatCard
          icon={BarChart3}
          title={'報告分析'}
          value={loading ? '載入中...' : '查看'}
          description={'查看系統報告與分析'}
          onClick={() => navigate('/reports')}
        />
        <StatCard
          icon={Clock}
          title={'即將到期'}
          value={loading ? '載入中...' : (systemStats.pendingDeadlines || '0')}
          description={systemStats.pendingDeadlines > 0 ? `${systemStats.pendingDeadlines} 個待辦項目即將到期` : '無待辦事項'}
          alertLevel={systemStats.pendingDeadlines > 3 ? 'danger' : systemStats.pendingDeadlines > 0 ? 'warning' : undefined}
          onClick={() => navigate('/case-management?filter=deadlines')}
        />
      </div>

      {/* 主要內容區域 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 左側 - 圖表區域 */}
        <div className="lg:col-span-2 space-y-6">
          {/* 月度趨勢圖 */}
          <Card>
            <CardHeader>
              <CardTitle>月度趨勢分析</CardTitle>
              <CardDescription>每月統計數據</CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="overview" className="space-y-4">
                <TabsList>
                  <TabsTrigger value="overview">總覽</TabsTrigger>
                  <TabsTrigger value="searches">搜尋</TabsTrigger>
                  <TabsTrigger value="cases">案件</TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="space-y-4">
                  {monthlyData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={monthlyData}>
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke={darkMode ? '#6b7280' : '#e5e7eb'}
                          opacity={0.5}
                        />
                        <XAxis
                          dataKey="month"
                          tick={{ fill: darkMode ? '#f9fafb' : '#374151', fontSize: 12 }}
                          axisLine={{ stroke: darkMode ? '#9ca3af' : '#6b7280' }}
                          tickLine={{ stroke: darkMode ? '#9ca3af' : '#6b7280' }}
                        />
                        <YAxis
                          tick={{ fill: darkMode ? '#f9fafb' : '#374151', fontSize: 12 }}
                          axisLine={{ stroke: darkMode ? '#9ca3af' : '#6b7280' }}
                          tickLine={{ stroke: darkMode ? '#9ca3af' : '#6b7280' }}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: darkMode ? '#374151' : '#ffffff',
                            border: `1px solid ${darkMode ? '#6b7280' : '#e5e7eb'}`,
                            borderRadius: '8px',
                            color: darkMode ? '#f9fafb' : '#374151',
                            fontSize: '12px'
                          }}
                          labelStyle={{
                            color: darkMode ? '#f9fafb' : '#374151',
                            fontWeight: '500'
                          }}
                        />
                        <Legend
                          wrapperStyle={{
                            color: darkMode ? '#f9fafb' : '#374151',
                            fontSize: '12px'
                          }}
                        />
                        <Line type="monotone" dataKey="searches" stroke="#3B82F6" strokeWidth={3} name="搜尋" />
                        <Line type="monotone" dataKey="cases" stroke="#10B981" strokeWidth={3} name="案件" />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-[300px] text-gray-500 dark:text-gray-400">
                      <div className="text-center">
                        <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>暫無統計數據</p>
                      </div>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="searches">
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={monthlyData}>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={darkMode ? '#6b7280' : '#e5e7eb'}
                        opacity={0.5}
                      />
                      <XAxis
                        dataKey="month"
                        tick={{ fill: darkMode ? '#f9fafb' : '#374151', fontSize: 12 }}
                        axisLine={{ stroke: darkMode ? '#9ca3af' : '#6b7280' }}
                        tickLine={{ stroke: darkMode ? '#9ca3af' : '#6b7280' }}
                      />
                      <YAxis
                        tick={{ fill: darkMode ? '#f9fafb' : '#374151', fontSize: 12 }}
                        axisLine={{ stroke: darkMode ? '#9ca3af' : '#6b7280' }}
                        tickLine={{ stroke: darkMode ? '#9ca3af' : '#6b7280' }}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: darkMode ? '#374151' : '#ffffff',
                          border: `1px solid ${darkMode ? '#6b7280' : '#e5e7eb'}`,
                          borderRadius: '8px',
                          color: darkMode ? '#f9fafb' : '#374151',
                          fontSize: '12px'
                        }}
                        labelStyle={{
                          color: darkMode ? '#f9fafb' : '#374151',
                          fontWeight: '500'
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="searches"
                        stroke="#3B82F6"
                        fill="#3B82F6"
                        fillOpacity={0.3}
                        strokeWidth={2}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </TabsContent>

                <TabsContent value="cases">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={monthlyData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="currentColor" opacity={0.3} />
                      <XAxis dataKey="month" tick={{ fill: 'currentColor' }} axisLine={{ stroke: 'currentColor' }} />
                      <YAxis tick={{ fill: 'currentColor' }} axisLine={{ stroke: 'currentColor' }} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'var(--card-background)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '8px',
                          color: 'var(--text-color)'
                        }}
                      />
                      <Bar dataKey="cases" fill="#10B981" />
                    </BarChart>
                  </ResponsiveContainer>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* 案件狀態分布 */}
          <Card>
            <CardHeader>
              <CardTitle>案件狀態分布</CardTitle>
              <CardDescription>當前案件統計</CardDescription>
            </CardHeader>
            <CardContent>
              {caseStatusData.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={caseStatusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {caseStatusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: darkMode ? '#374151' : '#ffffff',
                          border: `1px solid ${darkMode ? '#6b7280' : '#e5e7eb'}`,
                          borderRadius: '8px',
                          color: darkMode ? '#f9fafb' : '#374151',
                          fontSize: '12px'
                        }}
                        labelStyle={{
                          color: darkMode ? '#f9fafb' : '#374151',
                          fontWeight: '500'
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-3">
                    {caseStatusData.map((item, index) => {
                      // 計算案件狀態分布的總數
                      const totalStatusCases = caseStatusData.reduce((sum, statusItem) => sum + statusItem.value, 0);

                      return (
                        <div key={index} className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: item.color }}
                            ></div>
                            <span className="text-sm text-gray-600 dark:text-gray-300">
                              {item.name}
                            </span>
                          </div>
                          <div className="text-right">
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{item.value}</span>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {totalStatusCases > 0 ?
                                ((item.value / totalStatusCases) * 100).toFixed(1) :
                                '0.0'
                              }%
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-[200px] text-gray-500 dark:text-gray-400">
                  <div className="text-center">
                    <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="text-sm">載入案件狀態中</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* 右側 - 活動和狀態 */}
        <div className="space-y-6">
          {/* 系統狀態 */}
          <Card>
            <CardHeader>
              <CardTitle>{'系統狀態'}</CardTitle>
              <CardDescription>即時系統狀態</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-300">系統運行時間</span>
                  <span className="text-sm font-medium text-green-600 dark:text-green-400">
                    {loading ? '載入中...' : `${systemStats.systemUptime || 99.9}%`}
                  </span>
                </div>
                <Progress value={systemStats.systemUptime || 99.9} className="h-2" />
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-300">{'活躍用戶'}</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {loading ? '載入中...' : (systemStats.activeUsers || '0')}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Users className="h-4 w-4 text-blue-500 dark:text-blue-400" />
                  <span className="text-xs text-gray-500 dark:text-gray-400">當前線上用戶</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-green-50 dark:bg-green-900/30 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-500 dark:text-green-400" />
                    <span className="text-xs text-green-700 dark:text-green-300">AI服務</span>
                  </div>
                  <div className="text-sm font-medium text-green-800 dark:text-green-200">正常</div>
                </div>
                <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-blue-500 dark:text-blue-400" />
                    <span className="text-xs text-blue-700 dark:text-blue-300">資料庫</span>
                  </div>
                  <div className="text-sm font-medium text-blue-800 dark:text-blue-200">正常</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 最近活動 */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>最近活動</CardTitle>
                  <CardDescription>系統最新更新</CardDescription>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowBackendActivities(!showBackendActivities)}
                    className="flex items-center space-x-2"
                  >
                    {showBackendActivities ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    <span className="text-xs">{showBackendActivities ? '隱藏後端' : '顯示後端'}</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowSystemActivities(!showSystemActivities)}
                    className="flex items-center space-x-2"
                  >
                    {showSystemActivities ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    <span className="text-xs">{showSystemActivities ? '隱藏系統' : '顯示系統'}</span>
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="max-h-96 overflow-y-auto space-y-1">
                {(() => {
                  // 過濾活動邏輯
                  let filteredActivities = recentActivities

                  // 如果不顯示系統活動，則隱藏 user 為 'system' 的活動
                  if (!showSystemActivities) {
                    filteredActivities = filteredActivities.filter(activity => activity.user !== 'system')
                  }

                  // 如果隱藏後端活動，則排除後端相關的活動類型
                  if (showBackendActivities) {
                    const backendTypes = ['development', 'system']
                    filteredActivities = filteredActivities.filter(activity => !backendTypes.includes(activity.type))
                  }

                  return filteredActivities.length > 0 ? (
                    filteredActivities.map((activity) => (
                      <ActivityItem key={activity.id} activity={activity} />
                    ))
                  ) : (
                    <div className="flex items-center justify-center h-32 text-gray-500 dark:text-gray-400">
                      <div className="text-center">
                        <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">
                          {showBackendActivities
                            ? '沒有非後端活動記錄'
                            : showSystemActivities
                              ? '暫無最近活動'
                              : '沒有用戶活動記錄'}
                        </p>
                      </div>
                    </div>
                  )
                })()}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default Dashboard






